import { createContext, useContext, useMemo, useState } from "react";

const FloatingMicAssistantContext = createContext(null);

export const FloatingMicAssistantProvider = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isVoiceModeOpen, setIsVoiceModeOpen] = useState(false);
  // A question another page hands to the assistant. The chat lives inside
  // FloatingMicAssistant, which sends it and clears it.
  const [pendingPrompt, setPendingPrompt] = useState(null);

  // Chat drawer and voice overlay are mutually exclusive surfaces over the
  // same conversation state; opening one closes the other.
  const value = useMemo(
    () => ({
      isChatOpen,
      openChat: () => {
        setIsVoiceModeOpen(false);
        setIsChatOpen(true);
      },
      closeChat: () => setIsChatOpen(false),
      isVoiceModeOpen,
      openVoiceMode: () => {
        setIsChatOpen(false);
        setIsVoiceModeOpen(true);
      },
      closeVoiceMode: () => setIsVoiceModeOpen(false),
      pendingPrompt,
      askAssistant: (text) => {
        if (!text?.trim()) return;
        setIsVoiceModeOpen(false);
        setIsChatOpen(true);
        setPendingPrompt(text.trim());
      },
      clearPendingPrompt: () => setPendingPrompt(null),
    }),
    [isChatOpen, isVoiceModeOpen, pendingPrompt],
  );

  return (
    <FloatingMicAssistantContext.Provider value={value}>
      {children}
    </FloatingMicAssistantContext.Provider>
  );
};

export const useFloatingMicAssistant = () => {
  const context = useContext(FloatingMicAssistantContext);

  if (!context) {
    throw new Error(
      "useFloatingMicAssistant must be used within FloatingMicAssistantProvider",
    );
  }

  return context;
};
